/**
 * Subscription Plan Icons — admin-selected lucide icon + optional badge color per plan.
 */

import React from 'react';
import { SubscriptionPlan } from '../../types/subscription';
import { ClickableTooltip } from './ClickableTooltip';
import { SubscriptionPlanIconGlyph, subscriptionPlanBadgeStyle } from '../../utils/subscriptionPlanIcon';

interface SubscriptionPlanIconsProps {
  planIds: number[];
  plans: SubscriptionPlan[];
  iconSize?: string;
  compact?: boolean;
  /** Smaller badge for library list-row cover overlay (~10% under `compact`). */
  mini?: boolean;
}

export const SubscriptionPlanIcons: React.FC<SubscriptionPlanIconsProps> = ({
  planIds,
  plans,
  iconSize = 'p-2.5',
  compact = false,
  mini = false,
}) => {
  const planIdsToShow = planIds?.length > 0 ? planIds : [];
  const planDetails =
    planIdsToShow.length > 0
      ? planIdsToShow
          .map((id) => {
            const plan = plans.find((p) => p.id === id);
            return plan
              ? { id: plan.id, name: plan.name, icon: plan.icon, color: plan.color }
              : null;
          })
          .filter((p): p is { id: number; name: string; icon?: string; color?: string } => p !== null)
      : [];

  const boxClass = mini
    ? 'inline-flex items-center justify-center rounded-md p-0.5 w-[1.85rem] h-[1.85rem] flex-shrink-0'
    : compact
      ? 'inline-flex items-center justify-center p-2 rounded-xl w-[2.25rem] h-[2.25rem] flex-shrink-0'
      : `inline-flex flex-col items-center gap-0.5 ${iconSize === 'p-3' ? 'p-2' : 'p-2'}`;
  const iconClass = mini ? 'w-3.5 h-3.5' : compact ? 'w-5 h-5' : 'w-4 h-4';
  const badgeBorder = 'border';

  if (planDetails.length === 0) {
    return (
      <ClickableTooltip tooltip="Free/Trial Subscribers">
        <span
          className={`${boxClass} bg-gray-100 dark:bg-gray-700/50 ${badgeBorder} border-gray-200 dark:border-gray-600 ${!compact && !mini ? 'rounded-md' : ''}`}
        >
          <SubscriptionPlanIconGlyph freeTier className={iconClass} />
        </span>
      </ClickableTooltip>
    );
  }

  const renderBadge = (plan: { id: number; name: string; icon?: string; color?: string }) => (
    <span
      className={`${boxClass} ${badgeBorder} border-gray-200 dark:border-gray-600 ${!compact && !mini && planDetails.length > 1 ? '' : compact || mini ? '' : 'rounded-md'}`}
      style={subscriptionPlanBadgeStyle(plan.color)}
    >
      <SubscriptionPlanIconGlyph icon={plan.icon ?? null} color={plan.color ?? null} className={iconClass} />
    </span>
  );

  if (compact || mini) {
    const plan = planDetails[0];
    return <ClickableTooltip tooltip={plan.name}>{renderBadge(plan)}</ClickableTooltip>;
  }

  return (
    <span className={`${boxClass} rounded-md`}>
      {planDetails.map((plan) => (
        <ClickableTooltip key={plan.id} tooltip={plan.name}>
          <span className="inline-flex items-center">{renderBadge(plan)}</span>
        </ClickableTooltip>
      ))}
    </span>
  );
};
